/**
 * The watchlist (Lumen-BE 0095, roadmap §11.5): the sites and cabinets this person follows, each
 * with what is open there now. Theirs alone; it grants nothing.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from './client';

export interface WatchItem {
  id: string;
  kind: 'site' | 'cabinet';
  targetId: string;
  folder: string | null;
  /** The site's name, or the cabinet's serial. */
  name: string;
  site: { id: string; name: string; region: string };
  open: number;
  worst: 'critical' | 'major' | 'minor' | 'unknown' | null;
  latestAt: string | null;
  createdAt: string;
}

const KEY = ['watchlist'] as const;
const refresh = (qc: QueryClient): void => {
  void qc.invalidateQueries({ queryKey: KEY });
  /* The board's Watchlist queue reads the same set. */
  void qc.invalidateQueries({ queryKey: ['alarms'] });
};

export function useWatchlist(enabled = true) {
  return useQuery({
    queryKey: KEY,
    enabled,
    queryFn: () => api.get<{ data: WatchItem[] }>('/watchlist'),
    select: (r) => r.data,
    /* What is open at them moves with the fleet; a minute, like My work. */
    refetchInterval: 60_000,
  });
}

export function useFollow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (target: { siteId: string } | { cabinetId: string }) => api.post<WatchItem>('/watchlist', target),
    onSuccess: () => refresh(qc),
  });
}

export function useUnfollow() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => api.del<void>(`/watchlist/${id}`), onSuccess: () => refresh(qc) });
}

export function useMoveToFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, folder }: { id: string; folder: string | null }) => api.patch<WatchItem>(`/watchlist/${id}`, { folder }),
    onSuccess: () => refresh(qc),
  });
}
